import {
  CHUNK_SIZE,
  RENDER_DISTANCE,
  WORLD_SEED,
  GRASS_SPRITE_COUNT,
  GRASS_FEATURE_SPRITE_COUNT,
  GRASS_FEATURE_CHANCE,
  GRASS_OBSTRUCTION_SPRITE_COUNT,
  GRASS_OBSTRUCTION_CHANCE,
  GRASS_DISPLAY_WIDTH,
  GRASS_DISPLAY_HEIGHT,
  TREE_SPRITE_COUNT,
  TREE_CHANCE,
  GRASS_SPRITES_PATCHY_SPRITE_COUNT,
  DIRT_SPRITE_COUNT,
  DIRT_FEATURE_SPRITE_COUNT,
  DIRT_FEATURE_CHANCE,
  PATH_SPACING,
} from '../constants';
import { cartToIso, isoDepth } from '../utils/IsoUtils';

/**
 * ChunkManager
 * ────────────
 * Owns the infinite procedural world.  Generates / destroys 16×16 tile
 * chunks around the player so only the visible neighbourhood is alive.
 */
export class ChunkManager {
  /**
   * @param {Phaser.Scene} scene – the GameScene that owns us
   */
  constructor(scene) {
    this.scene = scene;

    /** @type {Map<string, Phaser.GameObjects.Group>} */
    this.chunks = new Map();

    /** @type {Set<string>} world tiles that block movement */
    this.blocked = new Set();

    // Simple deterministic hash for tile → grass frame selection
    this.seed = WORLD_SEED;
  }

  /* ── public ──────────────────────────────────────────────────── */

  /**
   * Call every frame (or whenever the player crosses a chunk boundary).
   * @param {number} cartX – player cartesian X
   * @param {number} cartY – player cartesian Y
   */
  update(cartX, cartY) {
    const playerChunkX = Math.floor(cartX / CHUNK_SIZE);
    const playerChunkY = Math.floor(cartY / CHUNK_SIZE);

    const neededKeys = new Set();

    for (let dx = -RENDER_DISTANCE; dx <= RENDER_DISTANCE; dx++) {
      for (let dy = -RENDER_DISTANCE; dy <= RENDER_DISTANCE; dy++) {
        const cx = playerChunkX + dx;
        const cy = playerChunkY + dy;
        const key = `${cx},${cy}`;
        neededKeys.add(key);

        if (!this.chunks.has(key)) {
          this.generateChunk(cx, cy, key);
        }
      }
    }

    // Destroy chunks that are too far away
    for (const [key, group] of this.chunks) {
      if (!neededKeys.has(key)) {
        // Remove blocked tiles for this chunk
        const [cx, cy] = key.split(',').map(Number);
        for (let lx = 0; lx < CHUNK_SIZE; lx++) {
          for (let ly = 0; ly < CHUNK_SIZE; ly++) {
            this.blocked.delete(`${cx * CHUNK_SIZE + lx},${cy * CHUNK_SIZE + ly}`);
          }
        }
        group.clear(true, true);
        this.chunks.delete(key);
      }
    }
  }

  /* ── private ─────────────────────────────────────────────────── */

  /**
   * Deterministic hash for a tile position → integer.
   * Same tile always gets the same value.
   * @param {number} mod – result is in range [0, mod)
   */
  tileHash(tx, ty, mod = GRASS_SPRITE_COUNT) {
    let h = (tx * 374761393 + ty * 668265263 + this.seed) | 0;
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    h = h ^ (h >>> 16);
    return ((h >>> 0) % mod);
  }

  /**
   * Check whether a 2x2 group anchored at even coords (tx, ty) has a tree.
   */
  hasTree(tx, ty) {
    return (this.tileHash(tx + 8123, ty + 4567, 10000) / 10000) < TREE_CHANCE;
  }

  /**
   * Check whether a tile is inside any tree's 2x2 zone.
   */
  isInTreeZone(wx, wy) {
    const ox = wx - (((wx % 2) + 2) % 2); // even-aligned origin X
    const oy = wy - (((wy % 2) + 2) % 2); // even-aligned origin Y
    return this.hasTree(ox, oy);
  }

  /**
   * Check whether a tile should have an obstruction (and isn't in a tree zone).
   */
  isObstructed(tx, ty) {
    if (this.isInTreeZone(tx, ty)) return false;
    return (this.tileHash(tx + 3301, ty + 9137, 10000) / 10000) < GRASS_OBSTRUCTION_CHANCE;
  }

  /**
   * Compute which tiles in (or near) a chunk are dirt, using deterministic
   * random walks seeded from a coarse grid of path origins.
   */
  /**
   * Get the jittered origin position for a path grid cell.
   */
  getPathOrigin(gx, gy) {
    return {
      x: gx * PATH_SPACING + this.tileHash(gx + 1111, gy + 2222, PATH_SPACING),
      y: gy * PATH_SPACING + this.tileHash(gx + 3333, gy + 4444, PATH_SPACING),
    };
  }

  getDirtTilesForChunk(chunkX, chunkY) {
    const dirtTiles = new Set();
    const startX = chunkX * CHUNK_SIZE;
    const startY = chunkY * CHUNK_SIZE;
    const endX = startX + CHUNK_SIZE;
    const endY = startY + CHUNK_SIZE;

    const margin = PATH_SPACING * 2;
    const minGX = Math.floor((startX - margin) / PATH_SPACING);
    const maxGX = Math.floor((endX + margin) / PATH_SPACING);
    const minGY = Math.floor((startY - margin) / PATH_SPACING);
    const maxGY = Math.floor((endY + margin) / PATH_SPACING);

    // Draw a wobbly line between two points, recording tiles near this chunk
    const tracePath = (ax, ay, bx, by, seed) => {
      let cx = ax, cy = ay;
      const maxSteps = Math.abs(bx - ax) + Math.abs(by - ay) + 20;
      for (let step = 0; step < maxSteps; step++) {
        if (cx >= startX - 1 && cx < endX + 1 && cy >= startY - 1 && cy < endY + 1) {
          dirtTiles.add(`${cx},${cy}`);
        }
        if (cx === bx && cy === by) break;

        const dx = bx - cx;
        const dy = by - cy;
        const wobble = this.tileHash(seed + step * 3571, seed + step * 2903, 10);

        if (wobble < 2) {
          // 20% lateral wobble
          if (Math.abs(dx) > Math.abs(dy)) {
            cy += (this.tileHash(seed + step, seed - step, 2) === 0) ? 1 : -1;
          } else {
            cx += (this.tileHash(seed + step, seed - step, 2) === 0) ? 1 : -1;
          }
        } else {
          // 80% move toward target
          if (Math.abs(dx) >= Math.abs(dy)) {
            cx += dx > 0 ? 1 : -1;
          } else {
            cy += dy > 0 ? 1 : -1;
          }
        }
      }
    };

    // Connect each origin to its right and below neighbors
    for (let gx = minGX; gx <= maxGX; gx++) {
      for (let gy = minGY; gy <= maxGY; gy++) {
        const from = this.getPathOrigin(gx, gy);
        const right = this.getPathOrigin(gx + 1, gy);
        const below = this.getPathOrigin(gx, gy + 1);
        tracePath(from.x, from.y, right.x, right.y, gx * 10000 + gy);
        tracePath(from.x, from.y, below.x, below.y, gx * 10000 + gy + 50000);
      }
    }

    return dirtTiles;
  }

  generateChunk(chunkX, chunkY, key) {
    const group = this.scene.add.group();
    const dirtTiles = this.getDirtTilesForChunk(chunkX, chunkY);

    // ── Pass 1: ground tiles, features, obstructions ────────────
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      for (let ly = 0; ly < CHUNK_SIZE; ly++) {
        const worldTileX = chunkX * CHUNK_SIZE + lx;
        const worldTileY = chunkY * CHUNK_SIZE + ly;
        const { x, y } = cartToIso(worldTileX + 0.5, worldTileY + 0.5);
        const isDirt = dirtTiles.has(`${worldTileX},${worldTileY}`);

        const flipRoll = this.tileHash(worldTileX + 5431, worldTileY + 2087);

        if (isDirt) {
          // ── Dirt path tile (or dirt feature variant) ──
          const dirtFeatureRoll = this.tileHash(worldTileX + 6113, worldTileY + 8297);
          const isDirtFeature = (dirtFeatureRoll / DIRT_SPRITE_COUNT) < DIRT_FEATURE_CHANCE;
          const dirtKey = isDirtFeature ? 'dirt_features' : 'dirt';
          const dirtFrameCount = isDirtFeature ? DIRT_FEATURE_SPRITE_COUNT : DIRT_SPRITE_COUNT;
          const frame = this.tileHash(worldTileX, worldTileY) % dirtFrameCount;
          const tile = this.scene.add.image(x, y, dirtKey, frame);
          tile.setDisplaySize(GRASS_DISPLAY_WIDTH, GRASS_DISPLAY_HEIGHT);
          tile.setOrigin(0.5, 0.5);
          tile.setDepth(isoDepth(worldTileX, worldTileY));
          if (flipRoll % 2 === 0) tile.setFlipX(true);
          group.add(tile);
        } else {
          // Check if any cardinal neighbor is dirt → use patchy grass as transition
          const neighborDirt =
            dirtTiles.has(`${worldTileX - 1},${worldTileY}`) ||
            dirtTiles.has(`${worldTileX + 1},${worldTileY}`) ||
            dirtTiles.has(`${worldTileX},${worldTileY - 1}`) ||
            dirtTiles.has(`${worldTileX},${worldTileY + 1}`);

          let spriteKey, frame;
          if (neighborDirt) {
            spriteKey = 'grass_sprites_patchy';
            frame = this.tileHash(worldTileX, worldTileY) % GRASS_SPRITES_PATCHY_SPRITE_COUNT;
          } else {
            const featureRoll = this.tileHash(worldTileX + 7919, worldTileY + 4391);
            const isFeature = (featureRoll / GRASS_SPRITE_COUNT) < GRASS_FEATURE_CHANCE;
            spriteKey = isFeature ? 'grass_features' : 'grass';
            const frameCount = isFeature ? GRASS_FEATURE_SPRITE_COUNT : GRASS_SPRITE_COUNT;
            frame = this.tileHash(worldTileX, worldTileY) % frameCount;
          }

          const tile = this.scene.add.image(x, y, spriteKey, frame);
          tile.setDisplaySize(GRASS_DISPLAY_WIDTH, GRASS_DISPLAY_HEIGHT);
          tile.setOrigin(0.5, 0.5);
          tile.setDepth(isoDepth(worldTileX, worldTileY));
          if (flipRoll % 2 === 0) tile.setFlipX(true);
          group.add(tile);

          // Obstruction (only on grass, not on dirt)
          if (this.isObstructed(worldTileX, worldTileY)) {
            const obstFrame = this.tileHash(worldTileX + 1013, worldTileY + 6271, GRASS_OBSTRUCTION_SPRITE_COUNT);
            const obs = this.scene.add.image(x, y, 'grass_obstructions', obstFrame);
            obs.setDisplaySize(GRASS_DISPLAY_WIDTH, GRASS_DISPLAY_HEIGHT);
            obs.setOrigin(0.5, 0.5);
            obs.setDepth(isoDepth(worldTileX, worldTileY) + 0.5);
            if (flipRoll % 2 === 0) obs.setFlipX(true);
            group.add(obs);
            this.blocked.add(`${worldTileX},${worldTileY}`);
          }
        }
      }
    }

    // ── Pass 2: trees on even-aligned 2x2 groups ────────────────
    for (let lx = 0; lx < CHUNK_SIZE; lx += 2) {
      for (let ly = 0; ly < CHUNK_SIZE; ly += 2) {
        const tx = chunkX * CHUNK_SIZE + lx;
        const ty = chunkY * CHUNK_SIZE + ly;
        if (!this.hasTree(tx, ty)) continue;

        // Skip tree if any of its 2x2 footprint is on a dirt path
        if (dirtTiles.has(`${tx},${ty}`) || dirtTiles.has(`${tx+1},${ty}`) ||
            dirtTiles.has(`${tx},${ty+1}`) || dirtTiles.has(`${tx+1},${ty+1}`)) continue;

        // Position at the 2x2 center
        const { x, y } = cartToIso(tx + 0.5, ty + 0.5);
        const treeFrame = this.tileHash(tx + 2039, ty + 7741, TREE_SPRITE_COUNT);
        // Per-frame origins so each tree variant's trunk aligns with the tile center
        const treeOrigins = [
          [0.50, 0.80],  // frame 0: trunk at ~52% x, ~77% y
          [0.50, 0.80],  // frame 1: trunk at ~51% x, extends lower
          [0.50, 0.65],  // frame 2: trunk at ~42% x, ~62% y
          [0.48, 0.64],  // frame 3: trunk at ~48% x, ~64% y
        ];
        const [originX, originY] = treeOrigins[treeFrame];

        const tree = this.scene.add.image(x, y, 'trees', treeFrame);
        tree.setDisplaySize(GRASS_DISPLAY_WIDTH * 2.2, GRASS_DISPLAY_HEIGHT * 3.5);
        tree.setOrigin(originX, originY);
        tree.setDepth(isoDepth(tx + 0.5, ty + 0.5) + 1);
        if (this.tileHash(tx + 5431, ty + 2087) % 2 === 0) tree.setFlipX(true);
        group.add(tree);

        // Single blocked tile centered on the trunk
        this.blocked.add(`${tx},${ty}`);
      }
    }

    this.chunks.set(key, group);
  }

  destroy() {
    for (const [, group] of this.chunks) {
      group.clear(true, true);
    }
    this.chunks.clear();
  }
}
